import { useCallback } from 'react'

/**
 * Hook to push events/data to Google Tag Manager's dataLayer.
 *
 * @example
 * const { pushEvent, pushData } = useGTM()
 *
 * // Push a custom event
 * pushEvent('button_click', { label: 'Upload Artwork', category: 'Editor' })
 *
 * // Push arbitrary data
 * pushData({ userId: '123', plan: 'pro' })
 */
function useGTM() {
    const getDataLayer = useCallback(() => {
        window.dataLayer = window.dataLayer || []
        return window.dataLayer
    }, [])

    /**
     * Push a named event with optional extra payload to GTM dataLayer.
     * @param {string} event - GTM event name
     * @param {Object} [payload={}] - Additional data to include
     */
    const pushEvent = useCallback(
        (event, payload = {}) => {
            if (!event) {
                console.warn('[useGTM] pushEvent: event name is required')
                return
            }
            getDataLayer().push({ event, ...payload })
        },
        [getDataLayer]
    )

    /**
     * Push arbitrary data to GTM dataLayer (no event key required).
     * @param {Object} data - Data object to push
     */
    const pushData = useCallback(
        (data = {}) => {
            if (!data || typeof data !== 'object') {
                console.warn('[useGTM] pushData: data must be a non-null object')
                return
            }
            getDataLayer().push(data)
        },
        [getDataLayer]
    )

    return { pushEvent, pushData }
}

/**
 * Build a standard category payload for GTM events.
 * @param {Object} [category]
 * @returns {{ _id: string, slug: string }}
 */
export const buildCategoryPayload = (category) => ({
    _id: category?._id || '',
    slug: category?.slug || 'all',
})

/**
 * Build a standard product payload for GTM events.
 * @param {Object} [product]
 * @param {Array} [selectedVariants] - Override variants with selected/picked variants
 * @returns {{ _id: string, title: string, slug: string, sku: string, variants: Array }}
 */
export const buildProductPayload = (product, selectedVariants) => ({
    _id: product?._id || '',
    title: product?.title || '',
    slug: product?.slug || '',
    sku: product?.sku || '',
    variants: selectedVariants || product?.variants || [],
})

export default useGTM
